/** The connection string, the once it exists.
 *
 * The service stores a hash. This reply is the only time the plaintext is
 * anywhere outside the logger that will hold it, and no endpoint can fetch it
 * again — so the card says that in the kicker, says it again in the meta
 * line, and does not offer a "show" toggle that would imply otherwise. If the
 * card is lost, the answer is a new token, not a support request.
 *
 * The connection string comes first because it is the one-paste unit the
 * logger accepts: it carries the host as well as the token, and the token
 * never travels in a URL again after it is parsed — the logger splits it into
 * two settings and sends the token as a Bearer header from then on. The bare
 * `gts_…` is offered underneath for a logger whose host is already set, or a
 * script that only wants the header; both come from the same reply, so there
 * is still only the one chance to copy either.
 *
 * The card is a live region: it appears without the reader having moved, and
 * a secret that is only announced by having scrolled into view is a secret
 * somebody misses.
 */

import { Card, CopyButton, Kicker } from '@/components/ui';
import type { NewToken } from '@/lib/types';

export interface TokenRevealProps {
  token: NewToken;
}

export function TokenReveal({ token }: TokenRevealProps) {
  return (
    <Card
      as="section"
      elevation="md"
      label="Your new connection string"
      style={{ gap: 12 }}
    >
      <div role="status" aria-live="polite" style={{ display: 'contents' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <Kicker>shown once</Kicker>
          <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
            Copy this into the datalogger: Admin → Sync. It cannot be shown again — if you lose
            it, revoke this token and make another.
          </span>
        </div>

        <SecretRow
          caption="connection string"
          value={token.connection_string}
          title="Copy the connection string"
        />
        <SecretRow caption="token only" value={token.token} title="Copy just the token" />

        <div
          style={{
            display: 'flex',
            gap: 18,
            flexWrap: 'wrap',
            fontSize: 11.5,
            color: 'var(--color-neutral-600)',
          }}
        >
          <span>
            label <span style={{ color: 'var(--color-neutral-300)' }}>{token.label}</span>
          </span>
          <span>
            scopes{' '}
            <span style={{ color: 'var(--color-neutral-300)', fontFamily: 'var(--font-mono)' }}>
              {token.scopes.join(', ')}
            </span>
          </span>
          <span style={{ textWrap: 'pretty' }}>
            the secret never travels in a URL after this paste — the logger stores host and token
            separately and sends the token as a header
          </span>
        </div>
      </div>
    </Card>
  );
}

function SecretRow({ caption, value, title }: { caption: string; value: string; title: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}>{caption}</span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <code
          style={{
            flex: 1,
            minWidth: 0,
            display: 'block',
            padding: '10px 12px',
            background: 'var(--color-bg)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-divider)',
            fontFamily: 'var(--font-mono)',
            fontSize: 12.5,
            color: 'var(--color-accent-300)',
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </code>
        <CopyButton text={value} title={title} className="flex-none min-w-[88px] justify-center" />
      </div>
    </div>
  );
}

export default TokenReveal;
